<template>
    <li v-if="trip" class="transfer-trip" :class="[selected && 'selected']">
        <div class="transfer-trip-content">
            <p v-if="!selected" class="transfer-trip-description">
                <span v-html="sanitize(description)" />
                <span class="transfer-trip-icons">
                    <span v-for="(icon, index) in trip.icons" :key="index">
                        <Icon :type="icon" size="xxs" color="grey" />
                        <Icon
                            v-if="index < trip.icons.length - 1"
                            type="Arrow"
                            :rotate="180"
                            size="xxs"
                            color="grey" />
                    </span>
                </span>
            </p>

            <Selected
                v-if="selected"
                :product="selected"
                :trip="trip"
                :onChangeSelected="onViewPreSelection" />
            <ButtonSecondary
                v-else
                :ariaLabel="addTransferLabel"
                :onClick="onViewPreSelection">
                {{ t({ key: 'shared.add' }) }}
            </ButtonSecondary>
        </div>

        <Modal
            v-if="showModal"
            :title="t({ key: 'shared.add_transfer' })"
            :subTitle="modalSubTitle"
            :onClose="onClose"
            :onCloseOutside="false"
            :onBack="!hasError && hasBackFunction ? onBack : undefined"
            position="top"
            size="m">
            <Notification
                v-if="hasError"
                variant="negative"
                :title="t({ key: 'shared.something_went_wrong' })" />
            <template v-else>
                <Loading v-if="isLoading" />
                <PreSelection
                    v-else-if="viewState?.view === ViewStateType.PreSelection"
                    :trip="trip"
                    :onSearch="onInitiateSearch"
                    :onClose="onClose" />
                <PostSelection
                    v-else-if="
                        viewState?.view === ViewStateType.PostSelection && viewState.product
                    "
                    :product="viewState.product"
                    :trip="trip"
                    :onSaveError="setError"
                    :onClose="onClose" />
                <Selection
                    v-else-if="viewState?.view === ViewStateType.Selection && data?.Options"
                    :options="data.Options"
                    :trip="trip"
                    :onSelect="onSelect"
                    :onClose="onClose"
                    :onAdjustPickupTime="onViewPreSelection" />
            </template>
        </Modal>
    </li>
</template>

<script lang="ts">
    import { computed, defineComponent, nextTick, ref, watch } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { handleCustomError } from '@/config/errorHandling';
    import { endpoints } from '@/api/endpoints';
    import {
        getJourneyDescription,
        getTripLocationDescription,
        mapToTransferProduct,
    } from '@/helpers/transfer/transfer';
    import {
        TransferSearchFlow,
        TransferTrip,
        TransferTripStatus,
    } from '@/helpers/transfer/transfer.interfaces';
    import { TransferProduct } from '@/api/Cart/cart.interfaces';
    import {
        SearchTransferResponse,
        TransferOption,
    } from '@/api/Transfer/transfer.interfaces';
    import { useStore } from '@/store';
    import { sanitize } from '@shared/utils/sanitize';
    import { logStatsigCheckoutTransferInteraction } from '@/tracking/statsig/checkout.functions';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import Loading from '@ui/components/Loading/Loading.vue';
    import Icon from '@ui/components/Icon/Icon.vue';
    import Selection from './Selection/Selection.vue';
    import Selected from './Selected/Selected.vue';
    import PostSelection from './PostSelection/PostSelection.vue';
    import PreSelection from './PreSelection/PreSelection.vue';

    enum ViewStateType {
        PreSelection = 'PreSelection',
        Selection = 'Selection',
        PostSelection = 'PostSelection',
    }

    interface ViewState {
        view: ViewStateType;
        product: TransferProduct | null;
    }

    export default defineComponent({
        name: 'TransferTrip',
        methods: { t, sanitize },
        components: {
            ButtonSecondary,
            Selection,
            Selected,
            Modal,
            Icon,
            Notification,
            Loading,
            PostSelection,
            PreSelection,
        },
        props: { tripId: { type: String, required: true } },
        setup({ tripId }) {
            const store = useStore();

            const data = ref<SearchTransferResponse | null>(null);
            const viewState = ref<ViewState | null>(null);

            const trip = computed(
                (): TransferTrip => store.getters['checkout/getTripById'](tripId)
            );

            const hasBackFunction = computed<boolean>(
                () =>
                    (viewState.value?.view === ViewStateType.Selection &&
                        !isFlightFlow.value) ||
                    viewState.value?.view === ViewStateType.PostSelection
            );

            const description = computed<string>(() => {
                return t({
                    key: 'shared.from_a_to_b',
                    values: [
                        { value: getJourneyDescription(trip.value.from, true), bold: true },
                        { value: getJourneyDescription(trip.value.to, true), bold: true },
                    ],
                });
            });

            const selected = computed(
                (): TransferProduct =>
                    store.getters['cart/getTransferProductByTripId'](tripId)
            );

            const isFlightFlow = computed(
                () => trip.value.searchFlow === TransferSearchFlow.Flight
            );

            watch(
                () => trip.value.active,
                (newActive) => {
                    if (newActive) {
                        onSearchTransfers();
                    }
                }
            );

            function onSelect(option: TransferOption) {
                logStatsigCheckoutTransferInteraction('Select', option, trip.value);

                viewState.value = {
                    view: ViewStateType.PostSelection,
                    product: mapToTransferProduct(option, trip.value),
                };
            }

            function onInitiateSearch() {
                onViewSelection();
            }

            function onClose() {
                viewState.value = null;

                store.dispatch('checkout/setTransferTripActive', {
                    id: tripId,
                    active: false,
                });
            }

            function onBack() {
                switch (viewState.value?.view) {
                    case ViewStateType.PostSelection:
                        onViewSelection(viewState.value?.product?.OfferId);
                        break;
                    case ViewStateType.Selection:
                        onViewPreSelection();
                        break;
                    default:
                        break;
                }

                return undefined;
            }

            function scrollToPreviousTop(id: string | undefined) {
                if (!id) return;

                const option: HTMLElement | null = document.querySelector(
                    `[data-transfer-option="${id}"]`
                );
                if (!option) return;

                const container: HTMLElement | null =
                    document.querySelector('.modal-container');
                if (!container) return;

                container.scrollTo({
                    top: option.offsetTop,
                });
            }

            async function onSearchTransfers() {
                try {
                    setLoading();

                    const response = await endpoints.transfer.Search(trip.value.request);
                    if (response) {
                        data.value = response;
                        setSuccess();
                    } else {
                        throw new Error('Empty response');
                    }
                } catch (error) {
                    handleCustomError(`SearchTransfers ${error}`);
                    setError();
                }
            }

            function onViewPreSelection() {
                if (isFlightFlow.value) {
                    onViewSelection();
                    return;
                }

                viewState.value = {
                    view: ViewStateType.PreSelection,
                    product: null,
                };

                store.dispatch('checkout/setTransferTripActive', {
                    id: tripId,
                    active: false,
                });
            }

            function onViewSelection(id?: string) {
                logStatsigCheckoutTransferInteraction('Search', null, trip.value);

                viewState.value = {
                    view: ViewStateType.Selection,
                    product: null,
                };

                store.dispatch('checkout/setTransferTripActive', {
                    id: tripId,
                    active: true,
                });

                nextTick(() => {
                    scrollToPreviousTop(id);
                });
            }

            function setSuccess() {
                store.dispatch('checkout/setTransferTripStatus', {
                    id: tripId,
                    status: TransferTripStatus.LOADED,
                });
            }

            function setLoading() {
                store.dispatch('checkout/setTransferTripStatus', {
                    id: tripId,
                    status: TransferTripStatus.LOADING,
                });
            }

            function setError() {
                store.dispatch('checkout/setTransferTripStatus', {
                    id: tripId,
                    status: TransferTripStatus.ERROR,
                });
            }

            const showModal = computed<boolean>(() => Boolean(viewState.value));

            const isLoading = computed<boolean>(
                () => trip.value.status === TransferTripStatus.LOADING
            );

            const hasError = computed<boolean>(
                () => trip.value.status === TransferTripStatus.ERROR
            );

            const modalSubTitle = computed<string>(() => {
                const showType = viewState.value?.view === ViewStateType.PreSelection;
                return getTripLocationDescription(trip.value, showType);
            });

            const addTransferLabel = t({ key: 'shared.add_transfer' });

            return {
                trip,
                showModal,
                selected,
                data,
                description,
                setError,
                hasBackFunction,
                onBack,
                onClose,
                onViewPreSelection,
                onInitiateSearch,
                onSelect,
                isLoading,
                hasError,
                modalSubTitle,
                addTransferLabel,
                viewState,
                ViewStateType,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Trip.scss';
</style>
